
































import { mapState, mapActions } from 'vuex'
import { CLASSIFIED_SEARCH_NS } from '~/store/modules/shared/classifieds/search/state'
import CCustomClusterMap from '~/components/shared/configurable/map/CCustomClusterMap.vue'
import { LatLng } from 'leaflet'
import { debounce } from '~/utils/function'
import { CLASSIFIED_VIEW_NS } from '~/store/modules/shared/classifieds/view/state'
import {
  bounceToString,
  stringToBounce,
  getBounceFromPositions
} from '~/components/shared/configurable/map/leaflet'
import { Classified } from '~/models/classified/types'
import { toCamelCase } from '~/utils/object'
import { defineComponent } from '~/utils/nuxt3-migration'
import { faIconToSvg } from '~/utils/icon'
import { faMap } from '@fortawesome/free-solid-svg-icons'

export default defineComponent({
  components: { CCustomClusterMap },
  props: {
    hoveredClassified: { type: Object, default: null },
    isMobile: { type: Boolean, default: false },
    isFullClassifiedVisible: { type: Boolean, default: false },
    minZoom: { type: Number, default: undefined },
    frozenList: { type: Boolean, default: false },
    disableLegendEvents: { type: Boolean, default: true }
  },
  data() {
    return {
      isMapVisible: false,
      geolocation: null,
      bounds: null,
      leafletMarker: null,
      marker: null,
      markerClickEvent: null,
      countryButtonName: null,
      fetchWithDebounce: debounce(
        function(northEast, southWest) {
          this.fetchMarkersAndResults(northEast, southWest)
        },
        this.isMobile ? 1000 : 0
      ),
      maxBounds: {
        lat: { max: 46.5, min: 31 },
        lon: { max: 36, min: 16 }
      },
      selectedCountry: null,
      forceFetchClusterAndMarkers: false,
      disabledFlyToLocation: false,
      grGeolocation: '41.294317263x30.926513672,35.047986734x18.654785156',
      cyGeolocation: '35.864569607x34.722290039,34.152726980x31.096801758',
      zoomMapToSeller: false,
      disabledBounceChange: false
    }
  },
  computed: {
    ...mapState(CLASSIFIED_SEARCH_NS, {
      clusters: state => toCamelCase(state.map.clusters),
      markers: state => toCamelCase(state.map.markers),
      loadingMap: state => state.loading.map,
      loadingResults: state => state.loading.results,
      params: state => state.params
    }),
    ...mapState(CLASSIFIED_VIEW_NS, {
      classified: state => toCamelCase(state.classified),
      sellerType: state => state.seller?.type
    }),
    legend(): Array<{ text: string; color: string; exactLocation: boolean }> {
      if (this.isMobile) {
        return
      }
      return [
        {
          text: this.$i18n.t('exact address'),
          color: '#1e88e5',
          exactLocation: true
        },
        {
          text: this.$i18n.t('non exact address'),
          color: '#757575',
          exactLocation: false
        }
      ]
    },
    buttons(): [object] {
      if (this.isMobile) {
        return null
      }
      const buttonName =
        this.countryButtonName === 'CY'
          ? this.$i18n.t('greece')
          : this.$i18n.t('cyprus')

      return [
        {
          title: buttonName,
          position: 'bottomright',
          event: 'clicked:btnLocation',
          styles: {
            color: '#ffffff',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            fontSize: '1rem',
            cursor: 'pointer',
            marginRight: '0.625rem',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0.1rem 0.5rem 0 0'
          },
          html: `<div><span>${faIconToSvg(
            faMap
          )}</span><span>${buttonName}</span></div>`,
          tooltip: 'tooltip',
          callback: () => {
            if (this.countryButtonName) {
              this.onCountryClick(this.countryButtonName)
            } else {
              this.onCountryClick(
                this.getOppositeCountry(
                  this.$refs.customClusterMap.isCYVisible()
                )
              )
            }
          }
        }
      ]
    },
    classifiedData(): Classified {
      return this.classified && this.classified?.id === this.marker?.id
        ? this.classified
        : ({} as Classified)
    },
    mapHeightOnMobile(): string {
      if (process.client) {
        return this.isMobile ? `${window.screen.height - 2.8 * 16}px` : null
      }
      return null
    }
  },
  watch: {
    'params.geolocation'(geolocation, prevGeolocation) {
      if (geolocation?.length) {
        const geolocationBounce = stringToBounce(geolocation[0])
        const geolocationStringify = JSON.stringify(this.geolocation)
        if (
          geolocation[0] &&
          JSON.stringify(geolocationBounce) !== geolocationStringify &&
          geolocationStringify !== JSON.stringify(prevGeolocation)
        ) {
          this.geolocation = geolocationBounce
        }
      } else if (prevGeolocation?.length && !geolocation) {
        this.$refs.customClusterMap.fitBoundsByLocation(
          stringToBounce(this.grGeolocation)
        )
      }
    },
    clusters() {
      if (this.hasLocationChange) {
        if (!this.disabledFlyToLocation) {
          this.flyToLocation()
        }
        this.hasLocationChange = false
        this.disabledFlyToLocation = false
      }
      if (this.zoomMapToSeller) {
        this.handleZoomMapToSeller()
      }
    }
  },
  mounted() {
    const { geolocation, shop, country: countryParam } = this.params
    const country = countryParam ? countryParam[0] : null
    this.countryButtonName = country
    this.selectedCountry = country

    if (geolocation && !shop) {
      if (Array.isArray(geolocation)) {
        this.geolocation = stringToBounce(geolocation[0])
      } else {
        this.geolocation = stringToBounce(geolocation)
      }
    }
    this.bounds = {
      northEast: {
        lat: 41.294317263,
        lng: 30.926513672
      },
      southWest: {
        lat: 35.047986734,
        lng: 18.654785156
      }
    }
    this.isMapVisible = true
  },
  activated() {
    this.disabledBounceChange = false
  },
  deactivated() {
    this.disabledBounceChange = true
  },
  methods: {
    ...mapActions(CLASSIFIED_SEARCH_NS, {
      mapChanged: 'mapChanged',
      setLoadingMap: 'setLoadingMap'
    }),
    ...mapActions(CLASSIFIED_VIEW_NS, {
      clearClassified: 'clearClassified'
    }),
    onBoundsChange({ northEast, southWest, withDebounce }) {
      if (!this.disabledBounceChange) {
        const isCYVisible = this.$refs.customClusterMap.isCYVisible()
        const isGRVisible = this.$refs.customClusterMap.isGRVisible()
        this.countryButtonName = isCYVisible && !isGRVisible ? 'CY' : 'GR'
        if (withDebounce) {
          this.fetchWithDebounce(northEast, southWest)
        } else {
          this.fetchMarkersAndResults(northEast, southWest)
        }
        this.$emit('bounds-change')
      }
      if (this.zoomMapToSeller) {
        this.zoomMapToSeller = false
        this.disabledBounceChange = false
      }
    },

    onMarkerClick({ marker, leafletMarker, e }) {
      this.leafletMarker = leafletMarker
      this.markerClickEvent = e
      this.marker = null

      this.$emit('marker-click', { marker, leafletMarker, e })
      if (
        this.marker?.id !== marker.id &&
        !this.isMobile &&
        !this.isFullClassifiedVisible
      ) {
      }

      this.marker = marker
    },
    onCountryClick(country: string) {
      let stringBounds = this.grGeolocation
      if (country === 'GR') {
        // cyprus
        stringBounds = this.cyGeolocation
      } else {
        // greece
        stringBounds = this.grGeolocation
      }

      const bounds = stringToBounce(stringBounds)
      this.$refs.customClusterMap.fitToBounds(bounds, undefined, false)
      this.selectedCountry = this.getOppositeCountry(country)
    },
    onMapInitialized() {
      this.$emit('map-initialized')
      const isCYVisible = this.$refs.customClusterMap.isCYVisible()
      const isGRVisible = this.$refs.customClusterMap.isGRVisible()
      this.countryButtonName = !isGRVisible && isCYVisible ? 'CY' : null
      const { location2, location, q, shop, postcode } = this.params
      this.invdaliteSize()
      if (location || location2 || q || shop || postcode) {
        if (this.loadingMap) {
          this.setLocationStatus()
        } else {
          this.flyToLocation()
        }
      }
    },
    closeMarkerPopup() {
      if (this.markerClickEvent?.target) {
        this.markerClickEvent.target.closePopup()
      }
    },
    getOppositeCountry(country: string) {
      return country === 'GR' ? 'CY' : 'GR'
    },
    flyToLocation() {
      this.setLoadingMap(false)
      const positions = this.getMarkerAndClustersPositions()
      if (positions.length) {
        this.$refs.customClusterMap.fitBoundsByLocation(positions)
      } else {
        this.forceFetchClusterAndMarkers = true
        this.$refs.customClusterMap.flyTo([39, 22], 6, true)
      }
    },
    getMarkerAndClustersPositions(): Array<[]> {
      const positions = []
      if (this.markers.length) {
        this.markers.forEach(m => {
          positions.push(Object.values(m.geolocation))
        })
      }
      if (this.clusters.length) {
        this.clusters.forEach(c => {
          if (c?.center?.lat) {
            positions.push(Object.values(c.center))
          }
        })
      }
      return positions
    },
    setGeolocation(geolocation: []) {
      this.geolocation = geolocation
    },
    setLocationStatus(status: boolean = true, flyToLocation: boolean = true) {
      this.hasLocationChange = status
      this.disabledFlyToLocation = !flyToLocation
    },

    fetchMarkersAndResultsOnUnfreezeList() {
      this.forceFetchClusterAndMarkers = true
      const { northEast, southWest } = this.bounds
      this.fetchMarkersAndResults(northEast, southWest)
    },
    handleMoveStart() {
      this.setLoadingMap(true)
    },
    getBounds(): { northEast: LatLng; southWest: LatLng } {
      const bounds = this.$refs.customClusterMap.map.getBounds()
      return {
        northEast: bounds._northEast,
        southWest: bounds._southWest
      }
    },
    zoomToSeller() {
      this.zoomMapToSeller = true
    },
    handleZoomMapToSeller() {
      const bounds = getBounceFromPositions(
        this.getMarkerAndClustersPositions()
      )

      this.$refs.customClusterMap.fitToBounds(bounds)
    },
    invdaliteSize() {
      if (this.$refs?.customClusterMap?.invalidateSize) {
        this.$refs.customClusterMap.invalidateSize()
      }
    },
    invalidateSizeWithDisabledBounds() {
      this.disabledBounceChange = true
      this.invdaliteSize()
      this.$nextTick(() => {
        this.disabledBounceChange = false
      })
    },
    async fetchMarkersAndResults(northEast: LatLng, southWest: LatLng) {
      if (
        JSON.stringify(this.bounds) !==
          JSON.stringify({ northEast, southWest }) ||
        this.forceFetchClusterAndMarkers
      ) {
        this.forceFetchClusterAndMarkers = false
        const geolocation = bounceToString(northEast, southWest)
        await this.mapChanged({
          geolocation,
          country: this.selectedCountry,
          fetchClassifieds: !this.frozenList
        })
        this.bounds = { northEast, southWest }
      } else {
        this.setLoadingMap(false)
      }
    }
  }
})
